import { QUESTIONS, type Registration } from "../../shared";
import type { RegistrationState } from "../survey";
import { AnimatedNumber } from "../ui";
import { WEBINAR } from "../webinar";
import { RegistrationForm } from "./RegistrationForm";

/** The website's hero sheet, except that the post-its now announce the survey. */
function WelcomeArtwork() {
	return (
		<div className="relative mx-auto hidden w-40 py-4 sm:block" aria-hidden="true">
			<div className="doc-sheet -rotate-2 p-5 sm:p-5">
				<p className="text-sm font-bold leading-tight">Live-Umfrage</p>
				<p className="mt-0.5 text-[0.625rem] text-muted-foreground">
					{WEBINAR.host} · {WEBINAR.date}
				</p>
				<div className="mt-4 grid gap-2">
					<div className="doc-line h-1.5 w-4/5" />
					<div className="doc-line h-1.5" />
					<div className="doc-line h-1.5 w-3/5" />
					<div className="doc-line h-1.5" />
					<div className="doc-line h-1.5 w-2/3" />
					<div className="doc-line h-1.5 w-1/2" />
				</div>
				<div className="mt-6 flex items-end justify-between gap-2">
					<span className="doc-mark text-xs">{QUESTIONS.length} Fragen</span>
					<span className="doc-stamp -rotate-6 px-1.5 py-0.5 text-[0.5625rem]">Live</span>
				</div>
			</div>
			<p className="note absolute -top-3 -right-12 size-24 rotate-3 text-base">Dauert nur 2 Min.!</p>
			<p className="note note-cool absolute -bottom-4 -left-12 size-24 -rotate-3 text-base">Ergebnisse live!</p>
			<img
				src="/dist/brand/papernaut_icon.svg"
				alt=""
				className="drift absolute -right-10 -bottom-10 w-20 -rotate-6"
			/>
		</div>
	);
}

function OnBoard({ participants }: { participants: number }) {
	return (
		<span className="text-sm text-ice/80">
			{participants > 0 ? (
				<>
					<AnimatedNumber value={participants} /> {participants === 1 ? "ist" : "sind"} schon an Bord
				</>
			) : (
				"Seien Sie die erste Person an Bord"
			)}
		</span>
	);
}

type Props = {
	/** Null until registered; then the form gives way to a personal start button. */
	firstName: string | null;
	participants: number;
	connected: boolean;
	registration: RegistrationState;
	onRegister: (registration: Registration) => void;
	onStart: () => void;
};

export function Welcome({ firstName, participants, connected, registration, onRegister, onStart }: Props) {
	return (
		<section className="deck starfield screen-on rounded-[1.75rem] md:rounded-[2.25rem]">
			<div className="grid items-center gap-10 p-7 sm:grid-cols-[minmax(0,1fr)_12rem] sm:p-10">
				<div>
					<span className="deck-label mb-6">
						Live-Umfrage · {WEBINAR.host} · {WEBINAR.date}
					</span>
					<h1 className="display-title mb-5 text-white">{WEBINAR.title}.</h1>
					<p className="text-lg leading-relaxed text-ice">
						Bevor es losgeht: {QUESTIONS.length} kurze Fragen zu Ihrem Alltag mit Dokumenten,
						Ablage und KI. In rund zwei Minuten – und Sie sehen live, wie alle anderen antworten.
					</p>

					{firstName === null ? (
						<RegistrationForm state={registration} connected={connected} onRegister={onRegister}>
							<OnBoard participants={participants} />
						</RegistrationForm>
					) : (
						<>
							<p className="mt-6 text-lg font-semibold text-white">Schön, dass Sie an Bord sind, {firstName}!</p>
							<div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3">
								<button type="button" className="btn-launch px-6 py-3 text-base" onClick={onStart}>
									Mission starten
									<span aria-hidden="true">🚀</span>
								</button>
								<OnBoard participants={participants} />
							</div>
						</>
					)}
				</div>
				<WelcomeArtwork />
			</div>

			<dl className="grid gap-px border-t border-white/10 bg-white/10 sm:grid-cols-3">
				<div className="bg-space px-7 py-4 sm:px-10">
					<dt className="text-sm font-semibold text-white">{QUESTIONS.length} Fragen</dt>
					<dd className="mt-0.5 text-sm text-ice/70">meist nur ein Klick</dd>
				</div>
				<div className="bg-space px-7 py-4 sm:px-6">
					<dt className="text-sm font-semibold text-white">Live-Ergebnisse</dt>
					<dd className="mt-0.5 text-sm text-ice/70">nach jeder Antwort</dd>
				</div>
				<div className="bg-space px-7 py-4 sm:pr-10 sm:pl-6">
					<dt className="text-sm font-semibold text-white">Diskret</dt>
					<dd className="mt-0.5 text-sm text-ice/70">im Webinar nur Gesamtergebnisse</dd>
				</div>
			</dl>
		</section>
	);
}
